import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { MenuList, MenuItem, Paper, Divider } from "@mui/material";
import ContextMenu from "../../components/ContextMenu/ContextMenu";
import DeleteConfirmationDialog from "./ContextMenuActions/DeleteConfirmationDialog";
import LocalRevisionHistory from "./ContextMenuActions/LocalRevisionHistory";
import ServerRevisionHistory from "./ContextMenuActions/ServerRevisionHistory";
import ReloadTemplate from "./ContextMenuActions/ReloadTemplate";
import LockTemplates from "./ContextMenuActions/LockTemplates";
import UnlockTemplates from "./ContextMenuActions/UnlockTemplates";
import DeployTemplatesToServer from "./ContextMenuActions/DeployTemplatesToServer";

const MODALS = {
  DELETE_CONFIRMATION: "DELETE_CONFIRMATION",
  DEPLOY_TO_SERVER: "DEPLOY_TO_SERVER",
  RELOAD_TEMPLATE: "RELOAD_TEMPLATE",
  USER_SPACE_REVISION_HISTORY: "USER_SPACE_REVISION_HISTORY",
  SERVER_REVISION_HISTORY: "SERVER_REVISION_HISTORY",
  LOCK_SELECTED: "LOCK_SELECTED",
  UNLOCK_SELECTED: "UNLOCK_SELECTED",
};

function DeployContextMenu(props) {
  const { t } = useTranslation();
  const { permissions } = useSelector((state) => state.userPermissions);
  const [modalToOpen, setModalToOpen] = useState(null);
  const {
    targetId,
    contextMenuNode,
    contextMenuType,
    selectedKeys,
    selectedNodes,
    userSpaceChecked,
    serverChecked,
    onRefreshTree,
    urlParams,
  } = props;

  const handleClick = (contextItem) => setModalToOpen(contextItem);
  
  const showMenuOrNot = useMemo(() => {
    return contextMenuNode && contextMenuType;
  }, [contextMenuNode, contextMenuType]);

  // Helper function to check if any selected item is locked
  const hasLockedItems = useMemo(() => {
    if (!selectedNodes || selectedNodes.length === 0) {
      return contextMenuNode?.isLocked === true;
    }
    return selectedNodes.some(node => node.isLocked === true);
  }, [selectedNodes, contextMenuNode]);

  // Helper function to check if any selected item is unlocked
  const hasUnlockedItems = useMemo(() => {
    if (!selectedNodes || selectedNodes.length === 0) {
      return contextMenuNode?.isLocked !== true;
    }
    return selectedNodes.some(node => node.isLocked !== true);
  }, [selectedNodes, contextMenuNode]);

  // Helper function to check if user has admin rights
  const hasAdminRights = useMemo(() => {
    return permissions?.includes("ADMIN_RIGHTS") || permissions?.includes("ACCOUNT_ADMIN_RIGHTS");
  }, [permissions]);

  // Helper function to check if user can delete locked items
  const canDeleteLockedItems = useMemo(() => {
    // If user has admin rights, they can delete locked items
    if (hasAdminRights) {
      return true;
    }
    // If no locked items, general users can delete
    return !hasLockedItems;
  }, [hasAdminRights, hasLockedItems]);

  // Helper function to check if user can unlock items
  const canUnlockItems = useMemo(() => {
    // If user has admin rights, they can unlock any items
    if (hasAdminRights) {
      return hasLockedItems; // Only show if there are locked items to unlock
    }
    return hasLockedItems;
  }, [hasAdminRights, hasLockedItems]);

  const handleDeployToServer = () => {
    setModalToOpen(null);
    handleClick(MODALS.DEPLOY_TO_SERVER);
  };

  const handleReloadTemplate = () => {
    setModalToOpen(MODALS.RELOAD_TEMPLATE);
  };

  const handleConfirmReload = () => {
    // Call the refresh function to reload the tree data
    if (onRefreshTree) {
      onRefreshTree();
    }
  };

  const handleUserSpaceRevisionHistory = () => {
    setModalToOpen(null);
    handleClick(MODALS.USER_SPACE_REVISION_HISTORY);
  };

  const handleServerRevisionHistory = () => {
    setModalToOpen(null);
    handleClick(MODALS.SERVER_REVISION_HISTORY);
  };

  const handleLockSelected = () => {
    setModalToOpen(null);
    handleClick(MODALS.LOCK_SELECTED);
  };

  const handleUnlockSelected = () => {
    setModalToOpen(null);
    handleClick(MODALS.UNLOCK_SELECTED);
  };

  const handleDeleteSelected = () => {
    setModalToOpen(null);
    handleClick(MODALS.DELETE_CONFIRMATION);
  };

  return (
    <>
      {showMenuOrNot && (
        <ContextMenu targetId={targetId}>
          <Paper elevation={3} sx={{ width: "15rem" }}>
            <MenuList sx={{ paddingTop: 0, paddingBottom: 0 }}>
               {contextMenuType === "template" && [
                 ...(contextMenuNode?.server?.exists === true && hasUnlockedItems && contextMenuNode?.isDocument === false ? [
                   <MenuItem 
                     key="lock-selected" 
                     onClick={handleLockSelected}
                     disabled={!hasUnlockedItems}
                   >
                     <span className={"LOCK"}></span>
                     <span className={"margin-left-8"}>Lock Selected Item</span>
                   </MenuItem>
                 ] : []),
                 ...(contextMenuNode?.server?.exists === true && canUnlockItems && contextMenuNode?.isDocument === false ? [
                   <MenuItem 
                     key="unlock-selected" 
                     onClick={handleUnlockSelected}
                     disabled={!canUnlockItems}
                   >
                     <span className={"UNLOCK"}></span>
                     <span className={"margin-left-8"}>Unlock Selected Item</span>
                   </MenuItem>
                 ] : []),
                ...(contextMenuNode?.isDocument === false ? [
                <Divider key="divider-0" />
                ]:[]),
                <MenuItem 
                  key="delete-selected" 
                  onClick={handleDeleteSelected}
                  disabled={!canDeleteLockedItems}
                >
                  <span className={"SYMBOL_DELETE"}></span>
                  <span className={"margin-left-8"}>
                    Delete Selected Template{selectedKeys.length > 1 ? "s" : ""}
                  </span>
                </MenuItem>,
                
                ...(contextMenuNode?.isDocument === false ? [
                <Divider key="divider-1" />,
                <MenuItem key="reload-template" onClick={handleReloadTemplate}>
                  <span className={"RELOAD_INBOUND"}></span>
                  <span className={"margin-left-8"}>Reload Template</span>
                </MenuItem>,
                ...(selectedNodes?.length === 1 ? [ <MenuItem
                  key="user-space-history"
                  onClick={handleUserSpaceRevisionHistory}>
                  <span className={"REVISION_HISTORY"}></span>
                  <span className={"margin-left-8"}>
                    User Space Revision History
                  </span>
                </MenuItem>,
                <MenuItem
                   key="server-history"
                   onClick={handleServerRevisionHistory}>
                   <span className={"REVISION_HISTORY"}></span>
                   <span className={"margin-left-8"}>
                     Server Revision History
                   </span>
                 </MenuItem>] : [])
                 ]:[]),
                 ...(contextMenuNode?.server?.exists === false || (contextMenuNode?.server?.exists === true && contextMenuNode?.userspace?.exists === true) ? [
                   <MenuItem
                     key="deploy-to-server"
                     onClick={handleDeployToServer}
                   >
                     <span className={"SERVER"}></span>
                     <span className={"margin-left-8"}>Deploy to Server</span>
                   </MenuItem>
                 ] : [])
              ]}

              {contextMenuType === "folder" && [
                ...(serverChecked ? [
                  <MenuItem
                    key="lock-folder"
                    name={t("MNU_LOCK_SEL_ITEM")}
                    onClick={handleLockSelected}
                    >
                    <span className={"LOCK"}></span>
                    <span className={"margin-left-8"}>
                      {t("MNU_LOCK_SEL_ITEM")}
                    </span>
                  </MenuItem>,
                  <MenuItem
                    key="unlock-folder"
                    name={t("MNU_UNLOCK_SEL_ITEM")}
                    onClick={handleUnlockSelected}
                    >
                    <span className={"UNLOCK"}></span>
                    <span className={"margin-left-8"}>
                      {t("MNU_UNLOCK_SEL_ITEM")}
                    </span>
                  </MenuItem>,
                  <Divider key="folder-divider-1" />
                ] : []),
                <MenuItem
                  key="deploy-folder"
                  name={t("DEPLOY_TO_SERVER")}
                  onClick={handleDeployToServer}
                  disabled={!userSpaceChecked}>
                  <span className={"SERVER"}></span>
                  <span className={"margin-left-8"}>
                    {t("DEPLOY_TO_SERVER")}
                  </span>
                </MenuItem>,
                <MenuItem
                  key="delete-folder"
                  name={t("DELETE")}
                  onClick={handleDeleteSelected}>
                  <span className={"SYMBOL_DELETE"}></span>
                  <span className={"margin-left-8"}>{t("DELETE")}</span>
                </MenuItem>,
              ]}
            </MenuList>
          </Paper>
        </ContextMenu>
      )}

      <DeleteConfirmationDialog
        open={modalToOpen === MODALS.DELETE_CONFIRMATION}
        onClose={() => setModalToOpen(null)}
        selectedNodes={selectedNodes}
        onRefreshTree={onRefreshTree}
        urlParams={urlParams}
        userSpaceChecked={userSpaceChecked}
        serverChecked={serverChecked}
      />
      <ReloadTemplate
        open={modalToOpen === MODALS.RELOAD_TEMPLATE}
        onClose={() => setModalToOpen(null)}
        onConfirm={handleConfirmReload}
        contextMenuNode={contextMenuNode}
        selectedNodes={selectedNodes}
      />
      <LocalRevisionHistory
        open={modalToOpen === MODALS.USER_SPACE_REVISION_HISTORY}
        onClose={() => setModalToOpen(null)}
        node={contextMenuNode}
        onRefreshTree={onRefreshTree}
        urlParams={urlParams}
      />
      <ServerRevisionHistory
        open={modalToOpen === MODALS.SERVER_REVISION_HISTORY}
        onClose={() => setModalToOpen(null)}
        node={contextMenuNode}
        onRefreshTree={onRefreshTree}
        urlParams={urlParams}
      />
      <LockTemplates
        open={modalToOpen === MODALS.LOCK_SELECTED}
        onClose={() => setModalToOpen(null)}
        node={contextMenuNode}
        selectedNodes={selectedNodes}
        onRefreshTree={onRefreshTree}
        urlParams={urlParams}
        userSpaceChecked={userSpaceChecked}
        serverChecked={serverChecked}
      />
      <UnlockTemplates
        open={modalToOpen === MODALS.UNLOCK_SELECTED}
        onClose={() => setModalToOpen(null)}
        node={contextMenuNode}
        selectedNodes={selectedNodes}
        onRefreshTree={onRefreshTree}
        urlParams={urlParams}
        userSpaceChecked={userSpaceChecked}
        serverChecked={serverChecked}
      />
      <DeployTemplatesToServer
        open={modalToOpen === MODALS.DEPLOY_TO_SERVER}
        onClose={() => setModalToOpen(null)}
        node={contextMenuNode}
        selectedNodes={selectedNodes}
        onRefreshTree={onRefreshTree}
        urlParams={urlParams}
        userSpaceChecked={userSpaceChecked}
        serverChecked={serverChecked}
      />
    </>
  );
}

export default DeployContextMenu;
